import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent } from '@/components/ui/card';
import { 
  Check, 
  Ticket, 
  QrCode, 
  Users, 
  BarChart, 
  Shield,
  Zap,
  ArrowRight,
  Play,
  Phone,
  Mail,
  MapPin,
  Globe,
  Handshake,
  ExternalLink
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Helmet } from 'react-helmet-async';
import ReviewList from '@/components/ReviewList';
import Logo from '@/components/Logo';
import LanguageSelector from '@/components/LanguageSelector';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '@/contexts/LanguageContext';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LandingPage() {
  const navigate = useNavigate();
  const { currentLang, changeLanguage } = useLanguage();
  const { t } = useTranslation();
  const [registerForm, setRegisterForm] = useState({
    email: '',
    password: '',
    tenant_name: '',
    acceptedPrivacy: false,
    acceptedTerms: false,
    acceptedGDPR: false
  });
  const [registering, setRegistering] = useState(false);
  const [videoMuted, setVideoMuted] = useState(true);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [showPaymentMethodModal, setShowPaymentMethodModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [partners, setPartners] = useState([]);
  const videoRef = useRef(null);

  // Fetch partners on component mount
  useEffect(() => {
    const fetchPartners = async () => {
      try {
        const response = await axios.get(`${API_URL}/partners`);
        setPartners(response.data);
      } catch (error) {
        console.error('Error fetching partners:', error);
      }
    };
    fetchPartners();
  }, []);

  // Handle payment initiation - show payment method selection
  const handlePayment = async (planName) => {
    const token = localStorage.getItem('token');
    
    if (!token) {
      // If not logged in, redirect to register with plan info
      sessionStorage.setItem('selected_plan', planName);
      navigate('/register');
      return;
    }

    // Check plan type
    if (planName === 'Enterprise') {
      toast.info('Per il piano Enterprise, ti preghiamo di contattarci direttamente.');
      window.location.href = 'mailto:info@zonepass.it?subject=Enterprise Plan Inquiry';
      return;
    } else if (planName === 'Basic') {
      // Basic plan - free
      navigate('/dashboard');
      return;
    }

    // Show payment method selection modal
    setSelectedPlan(planName);
    setShowPaymentMethodModal(true);
  };

  // Process payment with selected method
  const processPayment = async (paymentMethod) => {
    setShowPaymentMethodModal(false);
    setProcessingPayment(true);

    const token = localStorage.getItem('token');
    
    // Map plan name to package_id
    let packageId = '';
    if (selectedPlan === 'Pro') {
      packageId = billingPeriod === 'monthly' ? 'pro_monthly' : 'pro_annual';
    } else if (selectedPlan === 'Full') {
      packageId = billingPeriod === 'monthly' ? 'full_monthly' : 'full_annual';
    }

    try {
      const originUrl = window.location.origin;
      
      // PayPal Checkout ONLY
      const response = await axios.post(
        `${API_URL}/payments/paypal/create`,
        {
          package_id: packageId,
          origin_url: originUrl
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      // Redirect to PayPal approval URL
      if (response.data.approval_url) {
        window.location.href = response.data.approval_url;
      } else {
        throw new Error('No PayPal approval URL received');
      }
    } catch (error) {
      console.error('Payment error:', error);
      toast.error(error.response?.data?.detail || 'Errore nell\'avvio del pagamento');
      setProcessingPayment(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    
    if (!registerForm.acceptedPrivacy || !registerForm.acceptedTerms || !registerForm.acceptedGDPR) {
      toast.error('Devi accettare Privacy Policy, Termini di Servizio e GDPR');
      return;
    }

    setRegistering(true);
    try {
      await axios.post(`${API_URL}/register`, {
        email: registerForm.email,
        password: registerForm.password,
        tenant_name: registerForm.tenant_name
      });

      toast.success('Registrazione completata! Accedi per iniziare.');
      navigate('/login');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante la registrazione');
      setRegistering(false);
    }
  };

  const features = [
    {
      icon: Ticket,
      title: "Biglietti Illimitati",
      description: "Crea tutte le tipologie di biglietto che vuoi con prezzi personalizzati"
    },
    {
      icon: QrCode,
      title: "QR Code Personalizzabili",
      description: "QR code con i tuoi colori, logo e immagini per ogni biglietto"
    },
    {
      icon: Users,
      title: "Guest List Digitale",
      description: "Gestisci invitati e ospiti con database contatti integrato"
    },
    {
      icon: BarChart,
      title: "Statistiche Real-Time",
      description: "Monitora entrate, uscite e presenze in tempo reale"
    },
    {
      icon: Shield,
      title: "GDPR Compliant",
      description: "Sicuro, protetto e conforme alle normative europee"
    },
    {
      icon: Zap,
      title: "Setup in 2 Minuti",
      description: "Crea il tuo primo evento in meno di 2 minuti"
    }
  ];

  const [billingPeriod, setBillingPeriod] = React.useState('monthly'); // monthly or annual
  
  const pricingPlans = [
    {
      name: "Basic",
      price: t('pricing.free'),
      priceMonthly: 0,
      priceAnnual: 0,
      period: t('pricing.forever'),
      description: t('pricing.basic.description'),
      features: [
        t('features.up_to', { count: 20 }),
        t('features.guest_list'),
        t('features.scanners', { count: 1 }),
        t('features.no_online'),
        t('features.stats_basic'),
        t('features.support_email')
      ],
      cta: t('pricing.cta.free'),
      highlight: false
    },
    {
      name: "Pro",
      price: billingPeriod === 'monthly' ? "€25" : "€285",
      priceMonthly: 25,
      priceAnnual: 285, // €25 x 12 x 0.95 = €285/anno
      period: billingPeriod === 'monthly' ? t('pricing.perMonth') : t('pricing.perYear'),
      description: t('pricing.pro.description'),
      features: [
        t('features.up_to', { count: 100 }),
        t('features.everything_plus', { plan: 'Basic' }),
        t('features.scanners_plural', { count: 3 }),
        t('features.online_streaming'),
        t('features.stats_advanced'),
        t('features.pdf_reports'),
        t('features.branding'),
        t('features.support_priority')
      ],
      cta: t('pricing.cta.start'),
      highlight: true,
      savings: billingPeriod === 'annual' ? "-5%" : null
    },
    {
      name: "Full",
      price: billingPeriod === 'monthly' ? "€50" : "€570",
      priceMonthly: 50,
      priceAnnual: 570, // €50 x 12 x 0.95 = €570/anno
      period: billingPeriod === 'monthly' ? t('pricing.perMonth') : t('pricing.perYear'),
      description: t('pricing.full.description'),
      features: [
        t('features.up_to', { count: 500 }),
        t('features.everything_plus', { plan: 'Pro' }),
        t('features.scanners_plural', { count: 6 }),
        t('features.online_streaming'),
        t('features.stats_advanced'),
        t('features.pdf_reports'),
        t('features.branding'),
        t('features.support_247')
      ],
      cta: t('pricing.cta.start'),
      highlight: false,
      savings: billingPeriod === 'annual' ? "-5%" : null
    },
    {
      name: "Enterprise",
      price: t('pricing.custom'),
      priceMonthly: null,
      priceAnnual: null,
      period: t('pricing.contact_us'),
      description: t('pricing.enterprise.description'),
      features: [
        t('features.unlimited_participants'),
        t('features.everything_plus', { plan: 'Full' }),
        t('features.unlimited_scanners'),
        t('features.custom_api'),
        t('features.custom_integrations'),
        t('features.dedicated_manager'),
        t('features.sla')
      ],
      cta: t('pricing.cta.contact'),
      highlight: false
    }
  ];

  return (
    <>
      <Helmet>
        <title>ZonePass - Sistema di Gestione Eventi e Biglietteria Professionale</title>
        <meta name="description" content="ZonePass è la piattaforma completa per gestire eventi di ogni dimensione. Biglietteria digitale, QR code personalizzabili, check-in multi-zona, statistiche real-time. Dal piccolo evento al festival con migliaia di persone." />
        <meta name="keywords" content="gestione eventi, biglietteria digitale, QR code eventi, check-in eventi, ticketing system, event management, gestione biglietti, scanner QR, eventi Italia" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content="ZonePass - Sistema di Gestione Eventi Professionale" />
        <meta property="og:description" content="Gestisci eventi di ogni dimensione con biglietteria digitale, QR code e check-in intelligente" />
        <meta property="og:url" content="https://zonepass.io" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="ZonePass - Gestione Eventi Professionale" />
        <meta name="twitter:description" content="Sistema completo di biglietteria e check-in per eventi" />
        
        {/* Structured Data per SEO e AI */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            "name": "ZonePass",
            "applicationCategory": "BusinessApplication",
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "EUR"
            },
            "description": "Sistema professionale di gestione eventi e biglietteria con QR code, check-in multi-zona e statistiche real-time",
            "operatingSystem": "Web Browser",
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": "4.8",
              "ratingCount": "150"
            }
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-white">
        {/* Header/Navigation */}
        <nav className="fixed top-0 w-full bg-white/95 backdrop-blur-sm border-b border-gray-200 z-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              <div className="flex items-center">
                <Logo size="md" linkTo="/" />
              </div>
              <div className="hidden md:flex items-center space-x-8">
                <Link to="/chi-siamo" className="text-gray-700 hover:text-purple-600 transition-colors">
                  Chi Siamo
                </Link>
                <Link to="/examples" className="text-gray-700 hover:text-purple-600 transition-colors">
                  Esempi
                </Link>
                <a href="#pricing" className="text-gray-700 hover:text-purple-600 transition-colors">
                  Prezzi
                </a>
                
                {/* Language Selector - Temporarily disabled */}
                {/* <LanguageSelector /> */}
                
                <Link to="/login">
                  <Button variant="outline">
                    Accedi
                  </Button>
                </Link>
                <a href="#register">
                  <Button className="bg-purple-600 hover:bg-purple-700">
                    Inizia Gratis
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </nav>

        {/* Hero Section with Video Background */}
        <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-16">
          {/* Video Background */}
          <div className="absolute inset-0 z-0">
            {/* Background Video - Responsive with Audio */}
            <video 
              ref={videoRef}
              autoPlay 
              loop 
              muted={videoMuted}
              playsInline
              controls={false}
              className="absolute inset-0 w-full h-full object-cover opacity-15"
              style={{
                minWidth: '100%',
                minHeight: '100%',
                objectFit: 'cover'
              }}
            >
              <source src="/hero-background.mp4" type="video/mp4" />
              Your browser does not support the video tag.
            </video>
            
            {/* Audio Toggle Button */}
            <button
              onClick={() => setVideoMuted(!videoMuted)}
              className="absolute bottom-4 right-4 z-30 bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white p-3 rounded-full transition-all"
              aria-label={videoMuted ? 'Attiva audio' : 'Disattiva audio'}
            >
              {videoMuted ? (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2" />
                </svg>
              ) : (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                </svg>
              )}
            </button>
            
            {/* Gradient Overlay */}
            <div className="absolute inset-0 bg-gradient-to-br from-purple-900/90 via-blue-900/80 to-pink-900/90 z-10"></div>
            
            {/* Floating animated elements */}
            <div className="absolute inset-0 overflow-hidden z-20">
              <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-purple-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob"></div>
              <div className="absolute top-1/3 right-1/4 w-64 h-64 bg-pink-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-2000"></div>
              <div className="absolute bottom-1/4 left-1/3 w-64 h-64 bg-blue-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-4000"></div>
            </div>
          </div>

          {/* Hero Content */}
          <div className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 text-center">
            {/* Logo Grande Centrato */}
            <div className="mb-2 flex justify-center">
              <img 
                src="/zonepass-logo-final.png" 
                alt="ZonePass Logo" 
                className="h-48 sm:h-56 md:h-64 lg:h-80 xl:h-96 w-auto drop-shadow-2xl"
              />
            </div>
            
            <h1 className="text-5xl md:text-7xl font-extrabold text-white mb-6 leading-tight">
              {t('landing.hero.title')}
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-400">
                {t('landing.hero.subtitle')}
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-200 mb-8 max-w-3xl mx-auto">
              {t('landing.hero.description')}
            </p>
            
            {/* CTA Buttons */}
            <div className="flex justify-center mb-12">
              <a href="#register">
                <Button size="lg" className="bg-white text-purple-600 hover:bg-gray-100 text-lg px-8 py-6">
                  {t('landing.hero.cta')}
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </a>
            </div>

            {/* Trust indicators */}
            <div className="flex flex-wrap items-center justify-center gap-8 text-white/80">
              <div className="flex items-center gap-2">
                <Check className="w-5 h-5 text-green-400" />
                <span>Gratis fino a 20 partecipanti</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="w-5 h-5 text-green-400" />
                <span>Setup in 2 minuti</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="w-5 h-5 text-green-400" />
                <span>GDPR Compliant</span>
              </div>
            </div>
          </div>
        </section>

        {/* Video Demo Section */}
        <section id="video-demo" className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Dal Piccolo Evento al Grande Festival
              </h2>
              <p className="text-xl text-gray-600">
                Scopri come ZonePass si adatta a qualsiasi tipo di evento
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {/* Small Event Video */}
              <Card className="overflow-hidden hover:shadow-xl transition-shadow">
                <div className="relative bg-gradient-to-br from-purple-400 to-pink-400 h-48 flex items-center justify-center">
                  <div className="text-white text-center">
                    <Users className="w-16 h-16 mx-auto mb-2" />
                    <p className="text-lg font-semibold">Eventi Piccoli</p>
                    <p className="text-sm opacity-90">20-100 persone</p>
                  </div>
                  <div className="absolute inset-0 bg-black/20 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity cursor-pointer">
                    <Play className="w-12 h-12 text-white" />
                  </div>
                </div>
                <CardContent className="p-6">
                  <h3 className="font-semibold text-lg mb-2">Feste Private & Meeting</h3>
                  <p className="text-sm text-gray-600">
                    Perfetto per compleanni, riunioni aziendali, cene di gala
                  </p>
                </CardContent>
              </Card>

              {/* Medium Event Video */}
              <Card className="overflow-hidden hover:shadow-xl transition-shadow border-2 border-purple-600">
                <div className="relative bg-gradient-to-br from-blue-400 to-purple-400 h-48 flex items-center justify-center">
                  <div className="text-white text-center">
                    <Ticket className="w-16 h-16 mx-auto mb-2" />
                    <p className="text-lg font-semibold">Eventi Medi</p>
                    <p className="text-sm opacity-90">100-1000 persone</p>
                  </div>
                  <div className="absolute inset-0 bg-black/20 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity cursor-pointer">
                    <Play className="w-12 h-12 text-white" />
                  </div>
                </div>
                <CardContent className="p-6">
                  <h3 className="font-semibold text-lg mb-2">Conferenze & Concerti</h3>
                  <p className="text-sm text-gray-600">
                    Ideale per eventi corporate, concerti club, workshop
                  </p>
                </CardContent>
              </Card>

              {/* Large Event Video */}
              <Card className="overflow-hidden hover:shadow-xl transition-shadow">
                <div className="relative bg-gradient-to-br from-pink-400 to-red-400 h-48 flex items-center justify-center">
                  <div className="text-white text-center">
                    <BarChart className="w-16 h-16 mx-auto mb-2" />
                    <p className="text-lg font-semibold">Eventi Grandi</p>
                    <p className="text-sm opacity-90">1000+ persone</p>
                  </div>
                  <div className="absolute inset-0 bg-black/20 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity cursor-pointer">
                    <Play className="w-12 h-12 text-white" />
                  </div>
                </div>
                <CardContent className="p-6">
                  <h3 className="font-semibold text-lg mb-2">Festival & Convention</h3>
                  <p className="text-sm text-gray-600">
                    Ottimizzato per festival, fiere, grandi convention
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Features Grid */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Tutto ciò che ti serve per gestire eventi
              </h2>
              <p className="text-xl text-gray-600">
                Una piattaforma completa con tutte le funzionalità professionali
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {features.map((feature, idx) => (
                <Card key={idx} className="hover:shadow-lg transition-shadow">
                  <CardContent className="p-6">
                    <feature.icon className="w-12 h-12 text-purple-600 mb-4" />
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">
                      {feature.title}
                    </h3>
                    <p className="text-gray-600">
                      {feature.description}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Pricing Section */}
        <section id="pricing" className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Prezzi Trasparenti per Ogni Esigenza
              </h2>
              <p className="text-xl text-gray-600">
                Inizia gratis, scala quando cresci
              </p>
              
              {/* Billing Period Toggle */}
              <div className="mt-8 flex items-center justify-center gap-4">
                <span className={`text-lg font-medium ${billingPeriod === 'monthly' ? 'text-gray-900' : 'text-gray-500'}`}>
                  Mensile
                </span>
                <button
                  onClick={() => setBillingPeriod(billingPeriod === 'monthly' ? 'annual' : 'monthly')}
                  className={`relative inline-flex h-8 w-14 items-center rounded-full transition-colors ${
                    billingPeriod === 'annual' ? 'bg-purple-600' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`inline-block h-6 w-6 transform rounded-full bg-white transition-transform ${
                      billingPeriod === 'annual' ? 'translate-x-7' : 'translate-x-1'
                    }`}
                  />
                </button>
                <span className={`text-lg font-medium ${billingPeriod === 'annual' ? 'text-gray-900' : 'text-gray-500'}`}>
                  Annuale
                </span>
                <span className="ml-2 inline-flex items-center rounded-full bg-green-100 px-3 py-1 text-sm font-semibold text-green-800">
                  Risparmia 5%
                </span>
              </div>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {pricingPlans.map((plan, idx) => (
                <Card 
                  key={idx} 
                  className={`relative ${plan.highlight ? 'border-2 border-purple-600 shadow-2xl scale-105' : ''}`}
                >
                  {plan.highlight && (
                    <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                      <span className="bg-purple-600 text-white px-4 py-1 rounded-full text-sm font-semibold">
                        PIÙ POPOLARE
                      </span>
                    </div>
                  )}
                  <CardContent className="p-8">
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                    <div className="mb-4">
                      {/* Mostra vecchio prezzo barrato se annuale */}
                      {billingPeriod === 'annual' && plan.priceMonthly && plan.priceAnnual && (
                        <div className="mb-1">
                          <span className="text-2xl font-semibold text-gray-400 line-through">€{plan.priceMonthly * 12}</span>
                        </div>
                      )}
                      <div>
                        <span className="text-4xl font-bold text-purple-600">{plan.price}</span>
                        <span className="text-gray-600 ml-2">{plan.period}</span>
                      </div>
                      {plan.savings && billingPeriod === 'annual' && (
                        <div className="mt-2">
                          <span className="inline-flex items-center rounded-full bg-green-100 px-3 py-1 text-sm font-bold text-green-800">
                            {plan.savings}
                          </span>
                        </div>
                      )}
                    </div>
                    <p className="text-gray-600 mb-6">{plan.description}</p>
                    <ul className="space-y-3 mb-8">
                      {plan.features.map((feature, fIdx) => (
                        <li key={fIdx} className="flex items-start gap-2">
                          <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                          <span className="text-gray-700">{feature}</span>
                        </li>
                      ))}
                    </ul>
                    <Button 
                      onClick={() => handlePayment(plan.name)}
                      disabled={processingPayment}
                      className={`w-full ${plan.highlight ? 'bg-purple-600 hover:bg-purple-700' : 'bg-gray-900 hover:bg-gray-800'} ${processingPayment ? 'opacity-50 cursor-not-allowed' : ''}`}
                      size="lg"
                    >
                      {processingPayment ? 'Elaborazione...' : plan.cta}
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
            
            {/* Comparison Table */}
            <div className="mt-16 max-w-5xl mx-auto">
              <h3 className="text-2xl font-bold text-center mb-8">Confronta i Piani</h3>
              <div className="bg-white rounded-lg shadow-lg overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Caratteristica</th>
                      <th className="px-6 py-4 text-center text-sm font-semibold text-gray-900">Basic</th>
                      <th className="px-6 py-4 text-center text-sm font-semibold text-purple-600 bg-purple-50">Pro</th>
                      <th className="px-6 py-4 text-center text-sm font-semibold text-gray-900">Full</th>
                      <th className="px-6 py-4 text-center text-sm font-semibold text-gray-900">Enterprise</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">Partecipanti Max</td>
                      <td className="px-6 py-4 text-sm text-center">20</td>
                      <td className="px-6 py-4 text-sm text-center bg-purple-50">100</td>
                      <td className="px-6 py-4 text-sm text-center">500</td>
                      <td className="px-6 py-4 text-sm text-center">Illimitati</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">Dispositivi Scanner</td>
                      <td className="px-6 py-4 text-sm text-center">1</td>
                      <td className="px-6 py-4 text-sm text-center bg-purple-50">3</td>
                      <td className="px-6 py-4 text-sm text-center">6</td>
                      <td className="px-6 py-4 text-sm text-center">Illimitati</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">Eventi Online/Streaming</td>
                      <td className="px-6 py-4 text-sm text-center text-red-500">✗</td>
                      <td className="px-6 py-4 text-sm text-center bg-purple-50 text-green-600">✓</td>
                      <td className="px-6 py-4 text-sm text-center text-green-600">✓</td>
                      <td className="px-6 py-4 text-sm text-center text-green-600">✓</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">QR Code Personalizzabili</td>
                      <td className="px-6 py-4 text-sm text-center text-green-600">✓</td>
                      <td className="px-6 py-4 text-sm text-center bg-purple-50 text-green-600">✓</td>
                      <td className="px-6 py-4 text-sm text-center text-green-600">✓</td>
                      <td className="px-6 py-4 text-sm text-center text-green-600">✓</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">Statistiche Avanzate</td>
                      <td className="px-6 py-4 text-sm text-center text-gray-400">Base</td>
                      <td className="px-6 py-4 text-sm text-center bg-purple-50 text-green-600">✓</td>
                      <td className="px-6 py-4 text-sm text-center text-green-600">✓</td>
                      <td className="px-6 py-4 text-sm text-center text-green-600">✓</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">Branding Personalizzato</td>
                      <td className="px-6 py-4 text-sm text-center text-red-500">✗</td>
                      <td className="px-6 py-4 text-sm text-center bg-purple-50 text-green-600">✓</td>
                      <td className="px-6 py-4 text-sm text-center text-green-600">✓</td>
                      <td className="px-6 py-4 text-sm text-center text-green-600">✓</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">Report PDF</td>
                      <td className="px-6 py-4 text-sm text-center text-red-500">✗</td>
                      <td className="px-6 py-4 text-sm text-center bg-purple-50 text-green-600">✓</td>
                      <td className="px-6 py-4 text-sm text-center text-green-600">✓</td>
                      <td className="px-6 py-4 text-sm text-center text-green-600">✓</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">Badge Stampabili PDF</td>
                      <td className="px-6 py-4 text-sm text-center text-red-500">✗</td>
                      <td className="px-6 py-4 text-sm text-center bg-purple-50 text-red-500">✗</td>
                      <td className="px-6 py-4 text-sm text-center text-green-600">✓</td>
                      <td className="px-6 py-4 text-sm text-center text-green-600">✓</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">Supporto</td>
                      <td className="px-6 py-4 text-sm text-center">Email</td>
                      <td className="px-6 py-4 text-sm text-center bg-purple-50">Prioritario</td>
                      <td className="px-6 py-4 text-sm text-center">Prioritario 24/7</td>
                      <td className="px-6 py-4 text-sm text-center">Manager Dedicato</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">API Personalizzate</td>
                      <td className="px-6 py-4 text-sm text-center text-red-500">✗</td>
                      <td className="px-6 py-4 text-sm text-center bg-purple-50 text-red-500">✗</td>
                      <td className="px-6 py-4 text-sm text-center text-red-500">✗</td>
                      <td className="px-6 py-4 text-sm text-center text-green-600">✓</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">SLA</td>
                      <td className="px-6 py-4 text-sm text-center">-</td>
                      <td className="px-6 py-4 text-sm text-center bg-purple-50">-</td>
                      <td className="px-6 py-4 text-sm text-center">-</td>
                      <td className="px-6 py-4 text-sm text-center">99.9%</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </section>

        {/* Reviews Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Cosa Dicono i Nostri Utenti
              </h2>
              <p className="text-xl text-gray-600">
                Feedback reali da organizzatori di eventi in tutta Italia
              </p>
            </div>

            <div className="max-w-6xl mx-auto">
              <ReviewList limit={12} />
            </div>
          </div>
        </section>

        {/* Registration Form */}
        <section id="register" className="py-20 bg-gray-50">
          <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
            <Card className="shadow-2xl">
              <CardContent className="p-8">
                <div className="text-center mb-8">
                  <h2 className="text-3xl font-bold text-gray-900 mb-2">
                    Inizia Gratis Oggi
                  </h2>
                  <p className="text-gray-600">
                    Nessuna carta di credito richiesta. Setup in 2 minuti.
                  </p>
                </div>

                <form onSubmit={handleRegister} className="space-y-6">
                  <div>
                    <Label htmlFor="tenant_name">Nome Organizzazione *</Label>
                    <Input
                      id="tenant_name"
                      value={registerForm.tenant_name}
                      onChange={(e) => setRegisterForm({...registerForm, tenant_name: e.target.value})}
                      required
                      placeholder="Es: La Tua Azienda"
                    />
                  </div>

                  <div>
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={registerForm.email}
                      onChange={(e) => setRegisterForm({...registerForm, email: e.target.value})}
                      required
                      placeholder="tua@email.com"
                    />
                  </div>

                  <div>
                    <Label htmlFor="password">Password *</Label>
                    <Input
                      id="password"
                      type="password"
                      value={registerForm.password}
                      onChange={(e) => setRegisterForm({...registerForm, password: e.target.value})}
                      required
                      placeholder="Minimo 8 caratteri"
                    />
                  </div>

                  {/* Privacy Checkboxes */}
                  <div className="space-y-3 border-t pt-4">
                    <div className="flex items-start gap-2">
                      <Checkbox
                        id="privacy"
                        checked={registerForm.acceptedPrivacy}
                        onCheckedChange={(checked) => setRegisterForm({...registerForm, acceptedPrivacy: checked})}
                      />
                      <label htmlFor="privacy" className="text-sm text-gray-700 cursor-pointer">
                        Accetto la <Link to="/privacy" className="text-purple-600 hover:underline">Privacy Policy</Link> *
                      </label>
                    </div>

                    <div className="flex items-start gap-2">
                      <Checkbox
                        id="terms"
                        checked={registerForm.acceptedTerms}
                        onCheckedChange={(checked) => setRegisterForm({...registerForm, acceptedTerms: checked})}
                      />
                      <label htmlFor="terms" className="text-sm text-gray-700 cursor-pointer">
                        Accetto i <Link to="/terms" className="text-purple-600 hover:underline">Termini di Servizio</Link> *
                      </label>
                    </div>

                    <div className="flex items-start gap-2">
                      <Checkbox
                        id="gdpr"
                        checked={registerForm.acceptedGDPR}
                        onCheckedChange={(checked) => setRegisterForm({...registerForm, acceptedGDPR: checked})}
                      />
                      <label htmlFor="gdpr" className="text-sm text-gray-700 cursor-pointer">
                        Accetto il trattamento dei dati secondo GDPR *
                      </label>
                    </div>
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full bg-purple-600 hover:bg-purple-700" 
                    size="lg"
                    disabled={registering}
                  >
                    {registering ? 'Registrazione...' : 'Crea Account Gratuito'}
                  </Button>

                  <p className="text-center text-sm text-gray-600">
                    Hai già un account?{' '}
                    <Link to="/login" className="text-purple-600 hover:underline font-medium">
                      Accedi
                    </Link>
                  </p>
                </form>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Technical Partners Section */}
        {partners.length > 0 && (
          <section className="py-20 bg-gray-50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-12">
                <div className="flex items-center justify-center gap-2 mb-4">
                  <Handshake className="w-8 h-8 text-blue-600" />
                  <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">
                    I Nostri Partner
                  </h2>
                </div>
                <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                  Collaboriamo con i migliori partner per offrirti servizi completi
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {partners.map((partner) => (
                  <Card key={partner.id} className="hover:shadow-xl transition-all duration-300 border-2 hover:border-blue-200">
                    <CardContent className="p-6">
                      <div className="flex flex-col items-center text-center space-y-4">
                        {/* Logo */}
                        <div className="w-full h-24 flex items-center justify-center bg-white rounded-lg p-4 border">
                          <img 
                            src={partner.logo_url} 
                            alt={partner.name}
                            className="max-w-full max-h-full object-contain"
                            onError={(e) => {
                              e.target.src = 'https://via.placeholder.com/200x100?text=' + encodeURIComponent(partner.name);
                            }}
                          />
                        </div>
                        
                        {/* Name & Type */}
                        <div className="w-full">
                          <h3 className="font-bold text-xl text-gray-900 mb-1">{partner.name}</h3>
                          <span className="inline-block px-3 py-1 text-xs bg-blue-100 text-blue-700 rounded-full">
                            {partner.type}
                          </span>
                        </div>

                        {/* Description */}
                        <p className="text-sm text-gray-600 leading-relaxed">
                          {partner.description}
                        </p>

                        {/* CTA Buttons */}
                        <div className="flex gap-3 w-full pt-2">
                          <Button
                            variant="default"
                            className="flex-1"
                            onClick={() => window.open(partner.website_url, '_blank')}
                          >
                            <ExternalLink className="w-4 h-4 mr-2" />
                            Visita Sito
                          </Button>
                          {partner.email && (
                            <Button
                              variant="outline"
                              className="flex-1"
                              onClick={() => window.location.href = `mailto:${partner.email}`}
                            >
                              <Mail className="w-4 h-4 mr-2" />
                              Contatta
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Footer */}
        <footer className="bg-gray-900 text-white py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-4 gap-8">
              <div>
                <h3 className="text-2xl font-bold mb-4">ZonePass</h3>
                <p className="text-gray-400">
                  Il sistema professionale per gestire eventi di ogni dimensione
                </p>
              </div>
              
              <div>
                <h4 className="font-semibold mb-4">Prodotto</h4>
                <ul className="space-y-2 text-gray-400">
                  <li><Link to="/examples" className="hover:text-white transition-colors">Esempi</Link></li>
                  <li><a href="#pricing" className="hover:text-white transition-colors">Prezzi</a></li>
                  <li><Link to="/chi-siamo" className="hover:text-white transition-colors">Chi Siamo</Link></li>
                </ul>
              </div>
              
              <div>
                <h4 className="font-semibold mb-4">Legale</h4>
                <ul className="space-y-2 text-gray-400">
                  <li><Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link></li>
                  <li><Link to="/terms" className="hover:text-white transition-colors">Termini di Servizio</Link></li>
                </ul>
              </div>
              
              <div>
                <h4 className="font-semibold mb-4">Contatti</h4>
                <ul className="space-y-2 text-gray-400">
                  <li className="flex items-center gap-2">
                    <Mail className="w-4 h-4" />
                    <a href="mailto:info@zonepass.io" className="hover:text-white transition-colors">
                      info@zonepass.io
                    </a>
                  </li>
                  <li className="flex items-center gap-2">
                    <Phone className="w-4 h-4" />
                    <span>+39 XXX XXX XXXX</span>
                  </li>
                </ul>
              </div>
            </div>
            
            <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
              <p>&copy; 2025 ZonePass. Tutti i diritti riservati.</p>
            </div>
          </div>
        </footer>
      </div>

      <style jsx>{`
        @keyframes blob {
          0%, 100% { transform: translate(0, 0) scale(1); }
          25% { transform: translate(20px, -50px) scale(1.1); }
          50% { transform: translate(-20px, 20px) scale(0.9); }
          75% { transform: translate(50px, 50px) scale(1.05); }
        }
        
        .animate-blob {
          animation: blob 7s infinite;
        }
        
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        
        .animation-delay-4000 {
          animation-delay: 4s;
        }
      `}</style>

      {/* Payment Method Selection Modal */}
      {showPaymentMethodModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black bg-opacity-50 backdrop-blur-sm"
            onClick={() => setShowPaymentMethodModal(false)}
          />
          
          {/* Modal Content */}
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Conferma Pagamento</h2>
            <p className="text-gray-600 mb-6">Procedi con PayPal per completare il pagamento del piano {selectedPlan}</p>
            
            <div className="space-y-3">
              {/* PayPal Option - ONLY */}
              <button
                onClick={() => processPayment('paypal')}
                disabled={processingPayment}
                className="w-full flex items-center gap-4 p-6 border-2 border-blue-500 bg-blue-50 rounded-xl hover:border-blue-600 hover:bg-blue-100 transition-all group"
              >
                <div className="w-16 h-16 bg-blue-200 rounded-lg flex items-center justify-center group-hover:bg-blue-300">
                  <svg className="w-12 h-12" viewBox="0 0 124 33" xmlns="http://www.w3.org/2000/svg">
                    <path fill="#003087" d="M46.211 6.749h-6.839a.95.95 0 0 0-.939.802l-2.766 17.537a.57.57 0 0 0 .564.658h3.265a.95.95 0 0 0 .939-.803l.746-4.73a.95.95 0 0 1 .938-.803h2.165c4.505 0 7.105-2.18 7.784-6.5.306-1.89.013-3.375-.872-4.415-.972-1.142-2.696-1.746-4.985-1.746zM47 13.154c-.374 2.454-2.249 2.454-4.062 2.454h-1.032l.724-4.583a.57.57 0 0 1 .563-.481h.473c1.235 0 2.4 0 3.002.704.359.42.469 1.044.332 1.906zM66.654 13.075h-3.275a.57.57 0 0 0-.563.481l-.145.916-.229-.332c-.709-1.029-2.29-1.373-3.868-1.373-3.619 0-6.71 2.741-7.312 6.586-.313 1.918.132 3.752 1.22 5.031.998 1.176 2.426 1.666 4.125 1.666 2.916 0 4.533-1.875 4.533-1.875l-.146.91a.57.57 0 0 0 .562.66h2.95a.95.95 0 0 0 .939-.803l1.77-11.209a.568.568 0 0 0-.561-.658zm-4.565 6.374c-.316 1.871-1.801 3.127-3.695 3.127-.951 0-1.711-.305-2.199-.883-.484-.574-.668-1.391-.514-2.301.295-1.855 1.805-3.152 3.67-3.152.93 0 1.686.309 2.184.892.499.589.697 1.411.554 2.317zM84.096 13.075h-3.291a.954.954 0 0 0-.787.417l-4.539 6.686-1.924-6.425a.953.953 0 0 0-.912-.678h-3.234a.57.57 0 0 0-.541.754l3.625 10.638-3.408 4.811a.57.57 0 0 0 .465.9h3.287a.949.949 0 0 0 .781-.408l10.946-15.8a.57.57 0 0 0-.468-.895z"/>
                    <path fill="#009cde" d="M94.992 6.749h-6.84a.95.95 0 0 0-.938.802l-2.766 17.537a.569.569 0 0 0 .562.658h3.51a.665.665 0 0 0 .656-.562l.785-4.971a.95.95 0 0 1 .938-.803h2.164c4.506 0 7.105-2.18 7.785-6.5.307-1.89.012-3.375-.873-4.415-.971-1.142-2.694-1.746-4.983-1.746zm.789 6.405c-.373 2.454-2.248 2.454-4.062 2.454h-1.031l.725-4.583a.568.568 0 0 1 .562-.481h.473c1.234 0 2.4 0 3.002.704.359.42.468 1.044.331 1.906zM115.434 13.075h-3.273a.567.567 0 0 0-.562.481l-.145.916-.23-.332c-.709-1.029-2.289-1.373-3.867-1.373-3.619 0-6.709 2.741-7.311 6.586-.312 1.918.131 3.752 1.219 5.031 1 1.176 2.426 1.666 4.125 1.666 2.916 0 4.533-1.875 4.533-1.875l-.146.91a.57.57 0 0 0 .564.66h2.949a.95.95 0 0 0 .938-.803l1.771-11.209a.571.571 0 0 0-.565-.658zm-4.565 6.374c-.314 1.871-1.801 3.127-3.695 3.127-.949 0-1.711-.305-2.199-.883-.484-.574-.666-1.391-.514-2.301.297-1.855 1.805-3.152 3.67-3.152.93 0 1.686.309 2.184.892.501.589.699 1.411.554 2.317zM119.295 7.23l-2.807 17.858a.569.569 0 0 0 .562.658h2.822c.469 0 .867-.34.939-.803l2.768-17.536a.57.57 0 0 0-.562-.659h-3.16a.571.571 0 0 0-.562.482z"/>
                    <path fill="#003087" d="M7.266 29.154l.523-3.322-1.165-.027H1.061L4.927 1.292a.316.316 0 0 1 .314-.268h9.38c3.114 0 5.263.648 6.385 1.927.526.6.861 1.227 1.023 1.917.17.724.173 1.589.007 2.644l-.012.077v.676l.526.298a3.69 3.69 0 0 1 1.065.812c.45.513.741 1.165.864 1.938.127.795.085 1.741-.123 2.812-.24 1.232-.628 2.305-1.152 3.183a6.547 6.547 0 0 1-1.825 2c-.696.494-1.523.869-2.458 1.109-.906.236-1.939.355-3.072.355h-.73c-.522 0-1.029.188-1.427.525a2.21 2.21 0 0 0-.744 1.328l-.055.299-.924 5.855-.042.215c-.011.068-.03.102-.058.125a.155.155 0 0 1-.096.035H7.266z"/>
                    <path fill="#009cde" d="M23.048 7.667c-.028.179-.06.362-.096.55-1.237 6.351-5.469 8.545-10.874 8.545H9.326c-.661 0-1.218.48-1.321 1.132L6.596 26.83l-.399 2.533a.704.704 0 0 0 .695.814h4.881c.578 0 1.069-.42 1.16-.99l.048-.248.919-5.832.059-.32c.09-.572.582-.992 1.16-.992h.73c4.729 0 8.431-1.92 9.513-7.476.452-2.321.218-4.259-.978-5.622a4.667 4.667 0 0 0-1.336-1.03z"/>
                    <path fill="#012169" d="M21.754 7.151a9.757 9.757 0 0 0-1.203-.267 15.284 15.284 0 0 0-2.426-.177h-7.352a1.172 1.172 0 0 0-1.159.992L8.05 17.605l-.045.289a1.336 1.336 0 0 1 1.321-1.132h2.752c5.405 0 9.637-2.195 10.874-8.545.037-.188.068-.371.096-.55a6.594 6.594 0 0 0-1.017-.429 9.045 9.045 0 0 0-.277-.087z"/>
                    <path fill="#003087" d="M9.614 7.699a1.169 1.169 0 0 1 1.159-.991h7.352c.871 0 1.684.057 2.426.177a9.757 9.757 0 0 1 1.481.353c.365.121.704.264 1.017.429.368-2.347-.003-3.945-1.272-5.392C20.378.682 17.853 0 14.622 0h-9.38c-.66 0-1.223.48-1.325 1.133L.01 25.898a.806.806 0 0 0 .795.932h5.791l1.454-9.225 1.564-9.906z"/>
                  </svg>
                </div>
                <div className="flex-1 text-left">
                  <div className="font-bold text-gray-900 text-lg">PayPal</div>
                  <div className="text-sm text-gray-600">Pagamento sicuro con PayPal</div>
                </div>
                <div className="text-blue-600 text-2xl">→</div>
              </button>
            </div>

            {/* Close button */}
            <button
              onClick={() => setShowPaymentMethodModal(false)}
              className="mt-6 w-full text-gray-600 hover:text-gray-900 text-sm font-medium"
            >
              Annulla
            </button>
          </div>
        </div>
      )}
    </>
  );
}
